import { merge } from "lodash";
import { FieldType } from "./type-system/FieldType";

/**
 * The object describing all flag state for any context in the system
 * Flags are powered by LaunchDarkly. The LaunchDarkly flag is named after the path to the flag in this object, like `features.fieldTypes.Computed`.
 */
export type GadgetFlags = {
  features: {
    fieldTypes: {
      [FieldType.Computed]: boolean;
      [FieldType.Color]: boolean;
      [FieldType.Money]: boolean;
      [FieldType.Vector]: boolean;
    };
    preconditions: boolean;
    typescript: boolean;
    defaultLanguageSelection: boolean;
    darkMode: boolean;
    ideJSErrorChecking: boolean;
    frameworkVersions: boolean;
    javascriptPlaygroundLinks: boolean;
    actionsAsCode: boolean;
    fileBasedActions: boolean;
    aacMigration: boolean;
    computedViews: boolean;
    editorCodeGen: boolean;
    actionsExplainerPopup: boolean;
    stateInActionCode: boolean;
    liveQuery: boolean;
    viteFsAllowlist: boolean;
    emailPasswordAuthentication: boolean;
    codeEditorRightSideNavBar: boolean;
    appTemplates: boolean;
    codeMirrorEditor: boolean;
  };
  internal: {
    canarySandbox: boolean;
    stagingLoki: boolean;
    rateLimitMode: "off" | "warn" | "enforce";
    shopifyWebhookLoopDetection: boolean;
    upgradingFission: boolean;
    allowShopifyAccessTokenAPIAccess: boolean;
    smallerDeployActivities: boolean;
    dedicatedColumnsStrategy: boolean;
    apiKeysInBrowserClients: boolean;
    throttleExchange: boolean;
    liveQueryNotifications: boolean;
    edgeFrameworkVersion: boolean;
  };
  connections: {
    slack: boolean;
    twilio: boolean;
    sendgrid: boolean;
    gadget: boolean;
    appURL: boolean;
  };
};

/** Default flag state used for tests in state-trees and web */
export const TestFlagDefaultState: GadgetFlags = {
  features: {
    fieldTypes: {
      [FieldType.Computed]: false,
      [FieldType.Color]: false,
      [FieldType.Money]: false,
      [FieldType.Vector]: false,
    },
    preconditions: false,
    typescript: false,
    darkMode: false,
    defaultLanguageSelection: false,
    ideJSErrorChecking: false,
    frameworkVersions: false,
    javascriptPlaygroundLinks: false,
    actionsAsCode: false,
    fileBasedActions: false,
    aacMigration: false,
    computedViews: true,
    editorCodeGen: false,
    actionsExplainerPopup: false,
    stateInActionCode: false,
    liveQuery: false,
    viteFsAllowlist: false,
    emailPasswordAuthentication: false,
    codeEditorRightSideNavBar: false,
    appTemplates: false,
    codeMirrorEditor: false,
  },
  internal: {
    canarySandbox: false,
    stagingLoki: false,
    rateLimitMode: "enforce",
    shopifyWebhookLoopDetection: false,
    upgradingFission: false,
    allowShopifyAccessTokenAPIAccess: false,
    dedicatedColumnsStrategy: false,
    smallerDeployActivities: false,
    apiKeysInBrowserClients: true,
    throttleExchange: false,
    liveQueryNotifications: false,
    edgeFrameworkVersion: false,
  },
  connections: {
    slack: false,
    twilio: false,
    sendgrid: false,
    gadget: false,
    appURL: false,
  },
};

export const ClientSideDefaultFlags: GadgetFlags = merge({}, TestFlagDefaultState, {
  features: {
    editorCodeGen: false,
  },
});
